import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { RegionPreviewPayload } from "../../earth/regionPreview";
import {
  DEFAULT_PIXEL_CAPACITY,
  fetchAdminRegionDetail,
  fetchAdminRegions,
  updateAdminRegion,
  upsertGlobalRegion,
  type AdminRegion,
} from "../api";
import { resolvePrice } from "../../lib/price";
import type { AppCurrency } from "../../lib/localePrefs";

type PriceDraft = Record<AppCurrency, string>;

const CURRENCIES: AppCurrency[] = ["CHF", "EUR", "USD"];

function priceToDraft(properties?: AdminRegion["properties"]): PriceDraft {
  const raw = properties?.price;
  return {
    CHF:
      typeof raw === "object" && raw
        ? String(raw.CHF ?? "")
        : typeof raw === "number"
          ? String(raw)
          : "",
    EUR:
      typeof raw === "object" && raw ? String(raw.EUR ?? "") : "",
    USD:
      typeof raw === "object" && raw ? String(raw.USD ?? "") : "",
  };
}

function draftToPrice(draft: PriceDraft): Record<string, number> {
  const price: Record<string, number> = {};
  for (const code of CURRENCIES) {
    const n = parseFloat(draft[code].replace(",", "."));
    if (Number.isFinite(n)) price[code] = n;
  }
  return price;
}

function pixelCapacityToDraft(properties?: AdminRegion["properties"]): string {
  const raw = properties?.pixel_capacity;
  if (raw == null || raw === "") return String(DEFAULT_PIXEL_CAPACITY);
  return String(raw);
}

function draftToPixelCapacity(draft: string): number {
  const n = parseInt(draft.replace(/\s/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_PIXEL_CAPACITY;
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function RegionsPage() {
  const navigate = useNavigate();
  const [regions, setRegions] = useState<AdminRegion[]>([]);
  const [drafts, setDrafts] = useState<Record<string, PriceDraft>>({});
  const [capacityDrafts, setCapacityDrafts] = useState<Record<string, string>>(
    {},
  );
  const [globalDraft, setGlobalDraft] = useState<PriceDraft>(priceToDraft());
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [downloadId, setDownloadId] = useState<string | null>(null);

  const globalRegion = useMemo(
    () => regions.find((r) => r.is_global),
    [regions],
  );
  const localRegions = useMemo(
    () => regions.filter((r) => !r.is_global),
    [regions],
  );

  const load = useCallback(async () => {
    try {
      const data = await fetchAdminRegions();
      setRegions(data);
      const next: Record<string, PriceDraft> = {};
      const nextCapacity: Record<string, string> = {};
      for (const r of data) {
        next[r.id] = priceToDraft(r.properties);
        nextCapacity[r.id] = pixelCapacityToDraft(r.properties);
      }
      setDrafts(next);
      setCapacityDrafts(nextCapacity);
      const g = data.find((r) => r.is_global);
      setGlobalDraft(priceToDraft(g?.properties));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveRegion = async (region: AdminRegion) => {
    const draft = drafts[region.id];
    const capacityDraft = capacityDrafts[region.id];
    if (!draft) return;

    setSavingId(region.id);
    try {
      await updateAdminRegion(region.id, {
        name: region.name,
        priority: region.priority,
        properties: {
          ...(region.properties ?? {}),
          price: draftToPrice(draft),
          pixel_capacity: draftToPixelCapacity(
            capacityDraft ?? String(DEFAULT_PIXEL_CAPACITY),
          ),
        },
      });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const saveGlobal = async () => {
    setSavingId("global");
    try {
      await upsertGlobalRegion({
        name: globalRegion?.name ?? "Global",
        priority: globalRegion?.priority ?? 0,
        properties: { price: draftToPrice(globalDraft) },
      });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const downloadGeojson = async (region: AdminRegion) => {
    setDownloadId(region.id);
    setError(null);
    try {
      const detail = await fetchAdminRegionDetail(region.id);
      const ring = Array.isArray(detail.original_geojson)
        ? detail.original_geojson
        : [];
      if (ring.length < 3) {
        setError("Region has no polygon to export.");
        return;
      }
      const slug = detail.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      downloadJson(`${slug || "region"}-${detail.id.slice(0, 8)}.geojson`, {
        name: detail.name,
        id: detail.id,
        original_geojson: ring,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadId(null);
    }
  };

  const showOnMap = async (region: AdminRegion) => {
    if (region.is_global) {
      setError("Global region has no map boundary — it applies everywhere else.");
      return;
    }
    setPreviewId(region.id);
    setError(null);
    try {
      const detail = await fetchAdminRegionDetail(region.id);
      const ring = Array.isArray(detail.original_geojson)
        ? detail.original_geojson
        : [];
      if (ring.length < 3) {
        setError("Region has no polygon to display.");
        return;
      }
      const payload: RegionPreviewPayload = {
        id: detail.id,
        name: detail.name,
        is_global: detail.is_global,
        min_lat: detail.min_lat ?? -90,
        max_lat: detail.max_lat ?? 90,
        min_lng: detail.min_lng ?? -180,
        max_lng: detail.max_lng ?? 180,
        original_geojson: ring,
      };
      navigate("/world", { state: { regionPreview: payload } });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPreviewId(null);
    }
  };

  return (
    <div>
      <h1 className="admin-page-title">Regions</h1>
      <p className="admin-page-sub">
        Local regions use polygon priority. The global fallback applies when no
        polygon matches a pixel. Total pixel counts are set manually — download
        GeoJSON and run{" "}
        <code style={{ fontSize: "0.85em" }}>
          python backend/scripts/count_region_pixels.py &lt;file&gt;
        </code>{" "}
        locally, then enter the result below (default 1,000,000).
      </p>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-panel" style={{ marginBottom: "1.25rem" }}>
        <h3>Global fallback region</h3>
        <p style={{ color: "#8ba3b8", fontSize: "0.9rem", marginTop: 0 }}>
          Used when no higher-priority region polygon contains the pixel.
          {!globalRegion ? " Not configured yet — create one below." : null}
        </p>
        <div
          className="admin-table-wrap"
          style={{ marginTop: "0.75rem", border: "none" }}
        >
          <table className="admin-table">
            <thead>
              <tr>
                <th>CHF</th>
                <th>EUR</th>
                <th>USD</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                {CURRENCIES.map((code) => (
                  <td key={code}>
                    <input
                      className="admin-input"
                      value={globalDraft[code]}
                      onChange={(e) =>
                        setGlobalDraft((prev) => ({
                          ...prev,
                          [code]: e.target.value,
                        }))
                      }
                    />
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    disabled={savingId === "global"}
                    onClick={() => void saveGlobal()}
                  >
                    {savingId === "global"
                      ? "…"
                      : globalRegion
                        ? "Save global"
                        : "Create global"}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-toolbar">
        <button
          type="button"
          className="admin-btn admin-btn-ghost"
          onClick={() => void load()}
        >
          Refresh
        </button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Priority</th>
              <th>Vertices</th>
              <th>Total pixels</th>
              <th>CHF</th>
              <th>EUR</th>
              <th>USD</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {localRegions.length === 0 ? (
              <tr>
                <td colSpan={8}>No local regions defined.</td>
              </tr>
            ) : (
              localRegions.map((r) => {
                const draft = drafts[r.id] ?? priceToDraft(r.properties);
                const capacityDraft =
                  capacityDrafts[r.id] ?? pixelCapacityToDraft(r.properties);
                const preview = resolvePrice(r.properties?.price, "CHF");
                return (
                  <tr key={r.id}>
                    <td>
                      <input
                        className="admin-input"
                        value={r.name}
                        onChange={(e) =>
                          setRegions((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, name: e.target.value }
                                : x,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        type="number"
                        style={{ maxWidth: 80 }}
                        value={r.priority}
                        onChange={(e) =>
                          setRegions((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, priority: Number(e.target.value) }
                                : x,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>
                      {typeof r.original_geojson === "number"
                        ? r.original_geojson
                        : "—"}
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        type="text"
                        inputMode="numeric"
                        style={{ minWidth: 110 }}
                        value={capacityDraft}
                        onChange={(e) =>
                          setCapacityDrafts((prev) => ({
                            ...prev,
                            [r.id]: e.target.value,
                          }))
                        }
                        title={`Default ${DEFAULT_PIXEL_CAPACITY.toLocaleString()} if unset`}
                      />
                    </td>
                    {CURRENCIES.map((code) => (
                      <td key={code}>
                        <input
                          className="admin-input"
                          value={draft[code]}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [r.id]: { ...draft, [code]: e.target.value },
                            }))
                          }
                        />
                      </td>
                    ))}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary"
                          disabled={savingId === r.id}
                          onClick={() => void saveRegion(r)}
                        >
                          {savingId === r.id ? "…" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost"
                          disabled={downloadId === r.id}
                          onClick={() => void downloadGeojson(r)}
                        >
                          {downloadId === r.id ? "…" : "Download GeoJSON"}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost"
                          disabled={previewId === r.id}
                          onClick={() => void showOnMap(r)}
                        >
                          {previewId === r.id ? "…" : "Show on map"}
                        </button>
                      </div>
                      {preview > 0 ? (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#8ba3b8",
                            marginTop: 4,
                          }}
                        >
                          CHF preview {preview.toFixed(2)}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
